import { useCallback, useEffect, useRef } from "react";

/**
 * Keeps an outer tab's queue badge showing the **app-wide** open-pair count.
 *
 * The list endpoints return a `total_groups` filtered by whatever search the view has applied, so
 * publishing it unconditionally makes the badge read "Duplicates (1)" while the queue actually
 * holds dozens — and the wrong number sticks, because the badge lives in the parent and outlives
 * the tab. A filtered load must therefore not publish, and resolving a pair must re-read the
 * unfiltered count rather than decrementing the filtered one.
 *
 * `claim()` is called **before** starting a request and returns the publish function for it;
 * that publish is a no-op once a later request has claimed its slot, so a slow earlier response
 * can never overwrite a newer count. `fetchCount` takes no arguments and resolves to the
 * unfiltered `{ total_groups }` payload.
 */
export function useReviewQueueCount(fetchCount, onQueueChange) {
  const requestRef = useRef(0);

  // A tab switch or unmount invalidates anything still in flight.
  useEffect(() => () => {
    requestRef.current += 1;
  }, []);

  const claim = useCallback(() => {
    const requestId = ++requestRef.current;
    return (totalGroups) => {
      if (requestId === requestRef.current) onQueueChange?.(totalGroups || 0);
    };
  }, [onQueueChange]);

  /** Re-read the unfiltered count (after resolving a pair, where the list may be filtered). */
  const refresh = useCallback(async () => {
    const publish = claim();
    try {
      const data = await fetchCount();
      publish(data?.total_groups);
    } catch {
      // Best-effort: the badge keeps its previous value, the list stays authoritative.
    }
  }, [claim, fetchCount]);

  return { claim, refresh };
}
